import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { toast } from "sonner";

const statusOf = (it) => {
  if (!it) return "active";
  if (it.archived === true) return "archived";
  if (it.active === false) return "inactive";
  return "active";
};

const DEP_LABELS = {
  stock: "in-stock quantity", transactions: "ledger transactions", purchases: "purchase records",
  mappings: "room/template mappings", tagged_units: "tagged units", transfers: "transfers",
  room_overrides: "room par overrides",
  incidents: "incident records", maintenance: "maintenance jobs", opening_draft: "opening draft qty",
};

// C4: single audited Edit action (edit details + status Active/Inactive/Archived + safe delete/archive).
export const ItemEditDialog = ({
  open,
  onOpenChange,
  item,
  apiBase,
  title = "Edit Item",
  fields = [],
  onSaved,
  stockSummary,
  adjustmentHref,
  canDelete = true,
}) => {
  const [form, setForm] = useState({});
  const [status, setStatus] = useState("active");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (item) {
      const f = {};
      fields.forEach(fl => { f[fl.key] = item[fl.key] ?? ""; });
      setForm(f);
      setStatus(statusOf(item));
    }
  }, [item, open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!item) return null;
  const id = item.id;

  const save = async () => {
    setBusy(true);
    try {
      const patch = { status };
      fields.forEach(fl => {
        let v = form[fl.key];
        if (fl.type === "number") v = v === "" ? 0 : Number(v);
        patch[fl.key] = v;
      });
      await api.patch(`${apiBase}/${id}`, patch);
      toast.success("Item updated");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const del = async () => {
    const label = item.name || "this item";
    if (!window.confirm(`Permanently delete "${label}"?\n\nIf it has any stock, transactions, purchases or mappings it will be ARCHIVED instead of deleted (history is never lost). This cannot be undone if deleted.`)) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`${apiBase}/${id}`);
      if (data.deleted) {
        toast.success(`Deleted "${label}" permanently`);
      } else {
        const reasons = Object.entries(data.dependencies || {}).filter(([, v]) => v > 0)
          .map(([k, v]) => `${DEP_LABELS[k] || k}: ${v}`).join(", ");
        toast.info(`"${label}" was archived (not deleted) because it has ${reasons}.`);
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="item-edit-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {fields.map(fl => (
            <div key={fl.key}>
              <Label>{fl.label}</Label>
              <Input
                type={fl.type === "number" ? "number" : "text"}
                value={form[fl.key] ?? ""}
                onChange={e => setForm({ ...form, [fl.key]: e.target.value })}
                className="h-11 mt-1"
                data-testid={`edit-field-${fl.key}`}
              />
            </div>
          ))}
          {stockSummary && (
            <div className="border-t pt-3" data-testid="linen-item-stock-summary">
              <div className="text-sm font-semibold text-slate-800">Current Ledger Stock</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-2 text-sm">
                {[
                  ["Current Owned Total", stockSummary.accountable_total],
                  ["Store Clean", stockSummary.STORE_CLEAN],
                  ["Rooms / In Use", stockSummary.ROOM_CLEAN],
                  ["Dirty Holding", stockSummary.DIRTY_HOLDING],
                  ["External Laundry", stockSummary.EXTERNAL_LAUNDRY],
                  ["Returned Awaiting Storage", stockSummary.RETURNED_AWAITING],
                  ["Damaged / Quarantine", stockSummary.DAMAGED_QUARANTINE],
                ].map(([label, quantity]) => (
                  <div className="flex justify-between gap-2" key={label} data-testid={`item-stock-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <span className="text-slate-500">{label}</span>
                    <span className="font-mono text-slate-800">{quantity || 0}</span>
                  </div>
                ))}
              </div>
              {adjustmentHref && (
                <Button asChild variant="outline" className="mt-3" data-testid="open-current-stock-adjustment">
                  <a href={adjustmentHref}>Adjust Current Stock</a>
                </Button>
              )}
            </div>
          )}
          <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-11 mt-1" data-testid="edit-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            {status === "archived" && <div className="mt-1"><Badge className="bg-amber-100 border text-amber-800 text-[10px]">Hidden from operational lists; recoverable</Badge></div>}
          </div>
        </div>
        <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between">
          {canDelete && (
            <Button
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={del}
              disabled={busy}
              data-testid="edit-delete-btn"
            >
              Delete permanently
            </Button>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="edit-cancel-btn">Cancel</Button>
            <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={save} disabled={busy} data-testid="edit-save-btn">Save</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ItemEditDialog;
